import { Stack } from '@startuptools/ui/Stack';
import {
  GqlArticlesOfAssoc,
  GqlWarrantProgram,
  GqlWarrantProgramKind,
} from '../../../../../graphql/base-types.graphql';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { Select } from '@startuptools/ui/Select';
import { Grid } from '@startuptools/ui/Grid';
import { Option } from '@startuptools/ui/Option';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { IconButton } from '@startuptools/ui/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Table } from '@startuptools/ui/Table';
import { LabeledText } from '@startuptools/ui/LabeledText';
import {
  calculatedDefaultDuration,
  createDurationArray,
  DURATION_RANGE,
} from '../../../common/utils/create-duration-array';
import { toDateTimeMaybe } from '@startuptools/common/common';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { BigNumber } from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import { blackScholes } from '../../utils/black-scholes';
import { Duration } from 'luxon';
import { Button } from '@startuptools/ui/Button';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { AlertConfirmDialog } from '../../../../../components/simple-dialogs/AlertConfirmDialog';
import { useWpRefreshInterestRateMutation, useWpSetPlaygroundMutation } from '../../../../../graphql/react-operations';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';

const calculateDiscount = (illiquidityDiscount: string, pricePerWarrant: string) => {
  const discount = new BigNumber(pricePerWarrant).times(Number(illiquidityDiscount) / 100);
  const newValue = new BigNumber(pricePerWarrant).minus(discount).toString();
  return newValue;
};

type FormValues = {
  duration: number;
  volatility: string;
  illiquidityDiscount: string;
  riskFreeInterestRate: string;
  useRiksbankenRate: boolean;
  strikePrice: string;
  pricePerWarrant: string;
  discountPricePerWarrant: string;
};

export const WpStepPlayground = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'companyId'
    | 'warrantKind'
    | 'warrantStartDate'
    | 'warrantEndDate'
    | 'volatility'
    | 'illiquidityDiscount'
    | 'riskfreeInterestRate'
    | 'useRiksbankenRate'
    | 'strikePrice'
    | 'currency'
    | 'pricePerWarrant'
    | 'discountPricePerWarrant'
    | 'valuationCompanyValue'
    | 'valuationNumberOfShares'
  > & {
    articlesOfAssoc?: Pick<
      GqlArticlesOfAssoc,
      'id' | 'changeNumberOfShares' | 'numberOfSharesBefore' | 'numberOfSharesAfter'
    > | null;
  };
}) => {
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const [showRbRefresh, setShowRbRefresh] = useState(false);
  const durationMin = DURATION_RANGE[warrantProgram?.warrantKind ?? GqlWarrantProgramKind.Employee].min;
  const durationMax = DURATION_RANGE[warrantProgram?.warrantKind ?? GqlWarrantProgramKind.Employee].max;

  const currentDurationOptions = createDurationArray(
    durationMin,
    durationMax,
    false,
    0,
    toDateTimeMaybe(warrantProgram.warrantStartDate),
  );

  const { control, watch, setValue, reset, handleSubmit } = useForm<FormValues>({
    defaultValues: { useRiksbankenRate: warrantProgram.useRiksbankenRate ?? false },
  });

  const [doRefreshRb, { loading: rbRefreshLoading }] = useWpRefreshInterestRateMutation({
    variables: { companyId: warrantProgram.companyId, id: warrantProgram.id },
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const [doSubmit, { loading }] = useWpSetPlaygroundMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    reset({
      duration: calculatedDefaultDuration({
        warrantKind: warrantProgram.warrantKind,
        warrantStartDate: warrantProgram.warrantStartDate,
        warrantEndDate: warrantProgram.warrantEndDate,
      }),
      volatility: (Number(warrantProgram.volatility ?? 0) * 100).toString(),
      illiquidityDiscount: (Number(warrantProgram.illiquidityDiscount) * 100).toString(),
      riskFreeInterestRate: (Number(warrantProgram.riskfreeInterestRate) * 100).toString(),
      useRiksbankenRate: warrantProgram.useRiksbankenRate ?? false,
      strikePrice: warrantProgram.strikePrice ?? '',
      pricePerWarrant: warrantProgram.pricePerWarrant ?? '',
      discountPricePerWarrant: warrantProgram.discountPricePerWarrant ?? '',
    });
  }, [
    reset,
    warrantProgram.warrantKind,
    warrantProgram.warrantStartDate,
    warrantProgram.warrantEndDate,
    warrantProgram.volatility,
    warrantProgram.illiquidityDiscount,
    warrantProgram.riskfreeInterestRate,
    warrantProgram.useRiksbankenRate,
    warrantProgram.strikePrice,
    warrantProgram.pricePerWarrant,
    warrantProgram.discountPricePerWarrant,
  ]);

  const aoa = warrantProgram.articlesOfAssoc;

  const splitFactor = useMemo(
    () =>
      aoa?.changeNumberOfShares && aoa.numberOfSharesAfter
        ? new BigNumber(aoa.numberOfSharesAfter).div(aoa.numberOfSharesBefore)
        : new BigNumber(1),
    [aoa?.changeNumberOfShares, aoa?.numberOfSharesAfter, aoa?.numberOfSharesBefore],
  );
  const currentUseRbRate = watch('useRiksbankenRate');
  const currentStrikePrice = watch('strikePrice');
  const currentPricePerWarrant = watch('pricePerWarrant');
  const currentDiscountPricePerWarrant = watch('discountPricePerWarrant');

  const pricePerShare = useMemo(
    () =>
      warrantProgram.valuationNumberOfShares && warrantProgram.valuationCompanyValue
        ? new BigNumber(warrantProgram.valuationCompanyValue)
            .div(warrantProgram.valuationNumberOfShares)
            .div(splitFactor)
        : new BigNumber(0),

    [warrantProgram.valuationNumberOfShares, warrantProgram.valuationCompanyValue, splitFactor],
  );

  const companyValueAtStrike =
    warrantProgram.valuationNumberOfShares && currentStrikePrice
      ? new BigNumber(currentStrikePrice).times(warrantProgram.valuationNumberOfShares).times(splitFactor)
      : 0;

  const increasePercent =
    pricePerShare && currentStrikePrice
      ? new BigNumber(currentStrikePrice).minus(pricePerShare).div(pricePerShare).toNumber()
      : 0;

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type !== 'change' || !name) return;
      if (
        ['strikePrice', 'riskFreeInterestRate', 'volatility', 'duration', 'illiquidityDiscount'].includes(name) &&
        value.strikePrice &&
        value.riskFreeInterestRate &&
        value.volatility
      ) {
        const newPricePerWarrant = blackScholes(
          pricePerShare.toNumber(),
          Number(value.strikePrice),
          new BigNumber(value.riskFreeInterestRate).div(100).toNumber(),
          new BigNumber(value.volatility).div(100).toNumber(),
          Duration.fromObject({ months: Number(value.duration ?? 0) }).as('years'),
        );
        if (Number(value.pricePerWarrant ?? 0) !== newPricePerWarrant) {
          setValue('pricePerWarrant', newPricePerWarrant.toString());
        }
        const discount = calculateDiscount(value.illiquidityDiscount ?? '0', newPricePerWarrant.toString());
        if (value.discountPricePerWarrant !== discount) {
          setValue('discountPricePerWarrant', discount);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, currentPricePerWarrant, pricePerShare]);

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSubmit({
        variables: {
          companyId: warrantProgram.companyId,
          input: {
            id: warrantProgram.id,
            duration: values.duration,
            illiquidityDiscount: new BigNumber(values.illiquidityDiscount).div(100).toString(),
            pricePerWarrant: values.pricePerWarrant,
            riskfreeInterestRate: new BigNumber(values.riskFreeInterestRate).div(100).toString(),
            strikePrice: values.strikePrice,
            useRiksbankenRate: values.useRiksbankenRate,
            volatility: new BigNumber(values.volatility).div(100).toString(),
          },
        },
      });
      return stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
    return;
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3}>
          <Typography level="h4">
            <Trans id="Price of the option" />
          </Typography>
          <Grid container spacing={2} maxWidth={660} sx={{ flexGrow: 1 }}>
            <Grid xs={12} md={6}>
              <Controller
                control={control}
                name="duration"
                render={({ field: { onChange, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Duration" />
                    </FormLabel>
                    <Select {...field} onChange={(_, v) => onChange(v)} required>
                      {currentDurationOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.text}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <Controller
                control={control}
                name="strikePrice"
                render={({ field }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Strike Price" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      required
                      endDecorator={
                        <Stack direction="row" gap={1}>
                          {warrantProgram.currency}{' '}
                        </Stack>
                      }
                    />
                    <FormHelperText>
                      <Trans
                        id="Increase from current share price: {increasePercent}"
                        values={{
                          increasePercent: (
                            <NumberFormat locale={i18n.locale} variant="percentage">
                              {increasePercent}
                            </NumberFormat>
                          ),
                        }}
                      />
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <Controller
                control={control}
                name="volatility"
                render={({ field }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Volatility" />
                    </FormLabel>
                    <NumericFormatInput {...field} required endDecorator="%" />
                  </FormControl>
                )}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <Controller
                control={control}
                name="illiquidityDiscount"
                render={({ field }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Illiquidity Discount" />
                    </FormLabel>
                    <NumericFormatInput {...field} required endDecorator="%" />
                  </FormControl>
                )}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <Controller
                control={control}
                name="riskFreeInterestRate"
                render={({ field }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Risk Free Interest Rate" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      required
                      startDecorator={
                        <Tooltip title={<Trans id="Refresh the from the Riksbank" />}>
                          <IconButton onClick={() => setShowRbRefresh(true)}>
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      }
                      endDecorator="%"
                    />
                    <FormHelperText>
                      <Checkbox
                        checked={currentUseRbRate}
                        onChange={ev => setValue('useRiksbankenRate', ev.target.checked)}
                        size="sm"
                        label={<Trans id="Use Riksbank interest rate" />}
                      />
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid xs={12} md={6} alignItems="center">
              <LabeledText labelSx={{ fontSize: '14px' }} label={<Trans id="Price per Warrant before discount" />}>
                <NumberFormat
                  locale={i18n.locale}
                  variant="currency"
                  currency={warrantProgram.currency}
                  maximumFractionDigits={20}
                >
                  {currentPricePerWarrant}
                </NumberFormat>
              </LabeledText>
              <LabeledText labelSx={{ fontSize: '14px' }} label={<Trans id="Price per Warrant after discount" />}>
                <NumberFormat
                  locale={i18n.locale}
                  variant="currency"
                  currency={warrantProgram.currency}
                  maximumFractionDigits={20}
                >
                  {currentDiscountPricePerWarrant}
                </NumberFormat>
              </LabeledText>
            </Grid>

            <Grid sm={12} md={12} mt={3}>
              <Table variant="outlined" sx={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th />
                    <th align="right">
                      <Trans id="Price per Share" />
                    </th>
                    <th align="right">
                      <Trans id="Company Value" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>
                      <Trans id="Current valuation" /> {!splitFactor.isEqualTo(1) && <Trans id="(split adjusted)" />}
                    </th>
                    <td align="right">
                      <NumberFormat locale={i18n.locale} variant="currency" currency={warrantProgram.currency}>
                        {pricePerShare}
                      </NumberFormat>
                    </td>
                    <td align="right">
                      <NumberFormat locale={i18n.locale} variant="currency" currency={warrantProgram.currency}>
                        {warrantProgram.valuationCompanyValue}
                      </NumberFormat>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <Trans id="At strike" />
                    </th>
                    <td align="right">
                      <NumberFormat locale={i18n.locale} variant="currency" currency={warrantProgram.currency}>
                        {warrantProgram.strikePrice}
                      </NumberFormat>
                    </td>
                    <td align="right">
                      <NumberFormat locale={i18n.locale} variant="currency" currency={warrantProgram.currency}>
                        {companyValueAtStrike}
                      </NumberFormat>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Grid>
          </Grid>

          <Stack direction="row" gap={1}>
            <Button
              onClick={async () => {
                await stepNext();
                reset();
              }}
            >
              <Trans id="Continue without saving" />
            </Button>
            <Button type="submit" loading={loading}>
              <Trans id="Save and Continue" />
            </Button>
          </Stack>
        </Stack>
      </form>
      {showRbRefresh && (
        <AlertConfirmDialog
          sx={{ width: 600 }}
          loading={rbRefreshLoading}
          title={<Trans id="Refresh interest rate from Riksbanken" />}
          content={
            <Trans id="The interest rate may have changed, if the rate has changed it may affect the warrant price. Are you sure you want to refresh the interest rate?" />
          }
          onClose={() => setShowRbRefresh(false)}
          onConfirm={() => doRefreshRb()}
        />
      )}
    </>
  );
};
